import type { ConfigurationState, ModelData, ModelsData } from '../types'

// Direct option codes - each option gets a 2-character code
export interface DirectOptionCode {
  category: keyof ConfigurationState
  value: string
  code: string
  label: string
}

export const DIRECT_OPTION_CODES: DirectOptionCode[] = [
  // Variants
  { category: 'variant', value: 'standard', code: 'ST', label: 'Standard' },
  { category: 'variant', value: 'lift', code: 'LI', label: 'Lift' },
  
  // Handle sides
  { category: 'handleSide', value: 'left', code: 'HL', label: 'Handle Left' },
  { category: 'handleSide', value: 'right', code: 'HR', label: 'Handle Right' },
  
  // Side glass (for LIFT corner models)
  { category: 'sideGlass', value: 'left', code: 'SGL', label: 'Side Glass Left' },
  { category: 'sideGlass', value: 'right', code: 'SGR', label: 'Side Glass Right' },
  
  // Glass types
  { category: 'glass', value: 'single', code: 'S2', label: 'Single Glass' },
  { category: 'glass', value: 'double', code: 'S3', label: 'Double Glass' },
  { category: 'glass', value: 'triple', code: 'S11', label: 'Triple Glass' },
  { category: 'glass', value: 'single-connected', code: 'SC', label: 'Single Connected Glass' },
  { category: 'glass', value: 'double-connected', code: 'DC', label: 'Double Connected Glass' },
  
  // Ceramic options
  { category: 'ceramic', value: 'refrabox-light', code: 'W2', label: 'Refrabox Light' },
  { category: 'ceramic', value: 'cerabox-graphite', code: 'W5', label: 'Cerabox Graphite' },
  
  // Handle types
  { category: 'handle', value: 'classic-black', code: 'K1', label: 'Classic Black' },
  { category: 'handle', value: 'classic-silver', code: 'K2', label: 'Classic Silver' },
  { category: 'handle', value: 'design-black', code: 'K7', label: 'Design Black' },
  { category: 'handle', value: 'design-black-silver', code: 'K5', label: 'Design Black Silver' },
  
  // Bezel options - Standard variant
  { category: 'bezel', value: 'none', code: 'B0', label: 'No Bezel' },
  { category: 'bezel', value: 'flush-15', code: 'R15', label: 'Flush 15mm' },
  { category: 'bezel', value: 'flush-30', code: 'R30', label: 'Flush 30mm' },
  { category: 'bezel', value: 'recessed-15', code: 'RZ15', label: 'Recessed 15mm' },
  { category: 'bezel', value: 'recessed-30', code: 'RZ30', label: 'Recessed 30mm' },
  { category: 'bezel', value: 'recessed-15-45', code: 'RZ45', label: 'Recessed 15-45mm' },
  
  // Bezel options - Lift variant
  { category: 'bezel', value: 'recessed-lift-15', code: 'RL15', label: 'Lift Recessed 15mm' },
  { category: 'bezel', value: 'recessed-lift-30', code: 'RL30', label: 'Lift Recessed 30mm' },
  { category: 'bezel', value: 'recessed-lift-5', code: 'RL05', label: 'Lift Recessed 5mm' },
  
  // Fusion options
  { category: 'fusionBasic', value: 'fusion', code: 'F1', label: 'Fusion Basic' },
  { category: 'fusionBasic', value: 'none', code: 'F0', label: 'No Fusion' },
  { category: 'fusionModules', value: 'fusion-modules', code: 'FM', label: 'Fusion Modules' },
  { category: 'fusionModules', value: 'none', code: 'F0', label: 'No Fusion Modules' },
  
  // Fusion Corner options
  { category: 'fusionCorner', value: 'fusion-corner', code: 'FC', label: 'Fusion Corner' },
  { category: 'fusionCorner', value: 'none', code: 'FC0', label: 'No Fusion Corner' },
  { category: 'fusionCornerModules', value: 'fusion-corner-modules', code: 'FCM', label: 'Fusion Corner Modules' },
  { category: 'fusionCornerModules', value: 'none', code: 'FCM0', label: 'No Fusion Corner Modules' },
  
  // AKU Plus
  { category: 'akuPlus', value: 'aku-plus', code: 'AK', label: 'AKU Plus' },
  { category: 'akuPlus', value: 'none', code: 'A0', label: 'No AKU Plus' },
  
  // NEMO Thermal Protection
  { category: 'nemoThermalProtection', value: 'standard', code: 'NT0', label: 'NEMO Standard (no thermal protection)' },
  { category: 'nemoThermalProtection', value: 'with-protection', code: 'NT1', label: 'NEMO With Thermal Protection' },
  
  // MATERIAŁY AKUMULACYJNE
  { category: 'akuBox', value: 'aku-box', code: 'AB', label: 'AKU BOX' },
  { category: 'akuBox', value: 'none', code: 'AB0', label: 'No AKU BOX' },
  { category: 'easy700', value: 'easy-700', code: 'E7', label: 'EASY 700' },
  { category: 'easy700', value: 'none', code: 'E70', label: 'No EASY 700' },
  { category: 'cmaN', value: 'cma-n', code: 'CMN', label: 'CMA N' },
  { category: 'cmaN', value: 'none', code: 'CM0', label: 'No CMA N' },
  { category: 'cmaS', value: 'cma-s', code: 'CMS', label: 'CMA S' },
  { category: 'cmaS', value: 'none', code: 'CMS0', label: 'No CMA S' },
  
  // THERMAL MODULES
  { category: 'thermalModules', value: 'thermal', code: 'TM1', label: 'Thermal Module' },
  { category: 'thermalModules', value: 'none', code: 'TM0', label: 'No Thermal Modules' },
  
  // THERMAL HANGERS
  { category: 'thermalHangers', value: 'yes', code: 'ZW', label: 'Thermal Hangers' },
  { category: 'thermalHangers', value: 'none', code: 'ZW0', label: 'No Thermal Hangers' },
  
  // CONTROLLERS (sterowniki)
  { category: 'controller', value: 'none', code: 'CN', label: 'No Controller' },
  { category: 'controller', value: 'rt-08-om', code: 'C1', label: 'RT-08 OM' },
  { category: 'controller', value: 'rt-08-om-grafik', code: 'C2', label: 'RT-08 OM GRAFIK' },
  { category: 'controller', value: 'rt-08-om-grafik-wb', code: 'C3', label: 'RT-08 OM GRAFIK W/B' },
  { category: 'controller', value: 'rt-08-om-duo-integra', code: 'C4', label: 'RT-08 OM DUO INTEGRA' },
  { category: 'controller', value: 'rt-08-om-grafik-duo-integra', code: 'C5', label: 'RT-08 OM GRAFIK DUO INTEGRA' },
  { category: 'controller', value: 'rt-08-om-grafik-duo-integra-wb', code: 'C6', label: 'RT-08 OM GRAFIK DUO INTEGRA W/B' },
  // NEMO CONTROLLERS
  { category: 'controller', value: 'rt-08-g', code: 'NG1', label: 'RT-08G TITANIUM' },
  { category: 'controller', value: 'rt-08-g-wb', code: 'NG2', label: 'RT-08G WHITE / BLACK' },
  { category: 'controller', value: 'rt-08-g-x-wb', code: 'NG3', label: 'RT-08GX WHITE / BLACK' },
  // NEMO DUO INTEGRA CONTROLLERS
  { category: 'controller', value: 'rt-08-g-duo-integra', code: 'NG4', label: 'RT-08G DUO INTEGRA TITANIUM' },
  { category: 'controller', value: 'rt-08-g-wb-duo-integra', code: 'NG5', label: 'RT-08G DUO INTEGRA WHITE / BLACK' },
  { category: 'controller', value: 'rt-08-g-x-duo-integra', code: 'NG6', label: 'RT-08GX DUO INTEGRA TITANIUM' },
  { category: 'controller', value: 'rt-08-g-x-wb-duo-integra', code: 'NG7', label: 'RT-08GX DUO INTEGRA WHITE / BLACK' },
  
  // DOOR SENSOR (czujnik drzwiczek)
  { category: 'doorSensor', value: 'czujnik-drzwiczek', code: 'CD', label: 'Czujnik drzwiczek' },
  { category: 'doorSensor', value: 'none', code: 'CD0', label: 'No Door Sensor' },
  
  // CONTROLLER COLOR (dla sterowników z opcją white/black)
  { category: 'controllerColor', value: 'white', code: 'CW', label: 'Controller White' },
  { category: 'controllerColor', value: 'black', code: 'CB', label: 'Controller Black' },
  { category: 'controllerColor', value: 'none', code: 'CN0', label: 'No Controller Color' },
  
  // CONTROLLER BUFFOR (dla sterowników NEMO z wariantem BUFOR)
  { category: 'controllerBuffor', value: 'true', code: 'CBUF', label: 'Controller with BUFOR' },
  { category: 'controllerBuffor', value: 'false', code: 'CBUF0', label: 'Controller without BUFOR' },
  
  // Vis-a-vis Front side options
  { category: 'front-variant', value: 'standard', code: 'FS', label: 'Front Standard' },
  { category: 'front-variant', value: 'lift', code: 'FL', label: 'Front Lift' },
  { category: 'front-handleSide', value: 'left', code: 'FHL', label: 'Front Handle Left' },
  { category: 'front-handleSide', value: 'right', code: 'FHR', label: 'Front Handle Right' },
  { category: 'front-handle', value: 'classic-black', code: 'FH1', label: 'Front Classic Black' },
  { category: 'front-handle', value: 'classic-silver', code: 'FH2', label: 'Front Classic Silver' },
  { category: 'front-handle', value: 'design-black', code: 'FH3', label: 'Front Design Black' },
  { category: 'front-handle', value: 'design-black-silver', code: 'FH4', label: 'Front Design Black Silver' },
  { category: 'front-bezel', value: 'none', code: 'FB0', label: 'Front No Bezel' },
  { category: 'front-bezel', value: 'flush-15', code: 'FR15', label: 'Front Flush 15mm' },
  { category: 'front-bezel', value: 'flush-30', code: 'FR30', label: 'Front Flush 30mm' },
  { category: 'front-bezel', value: 'recessed-15', code: 'FRZ15', label: 'Front Recessed 15mm' },
  { category: 'front-bezel', value: 'recessed-30', code: 'FRZ30', label: 'Front Recessed 30mm' },
  { category: 'front-bezel', value: 'recessed-15-45', code: 'FRZ45', label: 'Front Recessed 15-45mm' },
  { category: 'front-bezel', value: 'recessed-lift-15', code: 'FRL15', label: 'Front Lift Recessed 15mm' },
  { category: 'front-bezel', value: 'recessed-lift-30', code: 'FRL30', label: 'Front Lift Recessed 30mm' },
  { category: 'front-bezel', value: 'recessed-lift-5', code: 'FRL05', label: 'Front Lift Recessed 5mm' },
  
  // Vis-a-vis Back side options
  { category: 'back-variant', value: 'standard', code: 'BS', label: 'Back Standard' },
  { category: 'back-variant', value: 'lift', code: 'BL', label: 'Back Lift' },
  { category: 'back-handleSide', value: 'left', code: 'BHL', label: 'Back Handle Left' },
  { category: 'back-handleSide', value: 'right', code: 'BHR', label: 'Back Handle Right' },
  { category: 'back-handle', value: 'classic-black', code: 'BH1', label: 'Back Classic Black' },
  { category: 'back-handle', value: 'classic-silver', code: 'BH2', label: 'Back Classic Silver' },
  { category: 'back-handle', value: 'design-black', code: 'BH3', label: 'Back Design Black' },
  { category: 'back-handle', value: 'design-black-silver', code: 'BH4', label: 'Back Design Black Silver' },
  { category: 'back-bezel', value: 'none', code: 'BB0', label: 'Back No Bezel' },
  { category: 'back-bezel', value: 'flush-15', code: 'BR15', label: 'Back Flush 15mm' },
  { category: 'back-bezel', value: 'flush-30', code: 'BR30', label: 'Back Flush 30mm' },
  { category: 'back-bezel', value: 'recessed-15', code: 'BRZ15', label: 'Back Recessed 15mm' },
  { category: 'back-bezel', value: 'recessed-30', code: 'BRZ30', label: 'Back Recessed 30mm' },
  { category: 'back-bezel', value: 'recessed-15-45', code: 'BRZ45', label: 'Back Recessed 15-45mm' },
  { category: 'back-bezel', value: 'recessed-lift-15', code: 'BRL15', label: 'Back Lift Recessed 15mm' },
  { category: 'back-bezel', value: 'recessed-lift-30', code: 'BRL30', label: 'Back Lift Recessed 30mm' },
  { category: 'back-bezel', value: 'recessed-lift-5', code: 'BRL05', label: 'Back Lift Recessed 5mm' },
]

// Create lookup maps for fast access
export const CODE_TO_DIRECT_OPTION = new Map<string, DirectOptionCode>()
export const OPTION_TO_DIRECT_CODE = new Map<string, string>()

DIRECT_OPTION_CODES.forEach(option => {
  CODE_TO_DIRECT_OPTION.set(option.code, option)
  const key = `${option.category}:${option.value}`
  OPTION_TO_DIRECT_CODE.set(key, option.code)
})

// Model group codes (2-character codes for each model group)
export const MODEL_GROUP_CODES = {
  'dragon-proste': 'DP',
  'dragon-proste-aku': 'DA',
  'dragon-portal': 'DL',
  'dragon-narozne': 'DN',
  'dragon-narozne-aku': 'DC',
  'dragon-trojstronne': 'DT',
  // Vis-a-vis (non-AKU) now uses VA
  'dragon-vis-a-vis': 'VA',
  // Vis-a-vis AKU uses DV
  'dragon-vis-a-vis-aku': 'DV',
  'dragon-polokragle': 'DR',
  'nemo-proste': 'NP',
  'nemo-narozne': 'NN',
  'nemo-vis-a-vis': 'NV'
}

// Reverse lookup for model groups
export const CODE_TO_MODEL_GROUP = Object.fromEntries(
  Object.entries(MODEL_GROUP_CODES).map(([group, code]) => [code, group])
)

// Model codes within groups (2-character codes for each model)
export const MODEL_CODES = {
  // Dragon Proste
  'dragon-2-slim': '2S',
  'dragon-2': '2R',
  'dragon-4': '4R',
  'dragon-2-xl': '2X',
  'dragon-4-slim': '4S',
  'dragon-4-xl-slim': '4Q',
  'dragon-4-xl': '4L',
  'dragon-6': '6R',
  'dragon-6-xl': '6X',
  'dragon-6-xxl': '6Z',
  'dragon-8-mini': '8M',
  'dragon-8-slim': '8S',
  'dragon-8': '8N',
  'dragon-8-xxl': '8Z',
  'dragon-9': '9R',
  'dragon-9-xl': '9X',
  'dragon-2-portal': '2P',
  'dragon-2-xl-portal': '2Q',
  'dragon-4-slim-portal': '4P',
  'dragon-4-slim-xl-portal': '4U',
  'dragon-4-portal': '4T',
  'dragon-4-xl-portal': '4W',
  'dragon-6-xl-portal': '6P',
  'dragon-6-xxl-portal': '6Q',
  
  // Dragon Proste AKU - używam różnych kodów niż zwykłe Dragon
  'dragon-aku-2': '2A',
  'dragon-aku-2-xl': '2Z',
  'dragon-aku-4-slim': '4K',
  'dragon-aku-4': '4J',
  'dragon-aku-6': '6J',
  'dragon-aku-6-xl': '6Y',
  'dragon-aku-9': '9J',
  'dragon-aku-8': '8J',
  
  // Dragon Narozne (2B models)
  'dragon-2b-slim': '2B',
  'dragon-2b': '2C',
  'dragon-2b-xl': '2D',
  'dragon-4b-slim': '4B',
  'dragon-4b': '4C',
  'dragon-4b-xl-slim': '4D',
  'dragon-4b-xl': '4E',
  'dragon-8b': '8B',
  'dragon-8b-xxl': '8C',
  
  // Dragon Narozne AKU (AKU 2B models)
  'dragon-aku-2b': '2E',
  'dragon-aku-2b-xl': '2F',
  'dragon-aku-4b-slim': '4F',
  'dragon-aku-4b': '4G',
  
  // Dragon Trojstronne (2C models)
  'dragon-2c': '2T',
  
  // Dragon Vis-a-vis
  'dragon-2-duo': '2V',
  'dragon-4-duo': '4V',
  'dragon-6-xxl-duo': '6V',
  'dragon-8-duo': '8V',
  'dragon-8-xxl-duo': '8X',
  'dragon-9-duo': '9V',
  'dragon-aku-4-duo': '4A',
  'dragon-aku-9-duo': '9A',
  'dragon-aku-8-duo': '8A',
  
  // Dragon Polokragle
  'dragon-8r': '8R',
  
  // Nemo Proste models
  'nemo-2': 'N2',
  'nemo-2-xl': 'N2X',
  'nemo-4-slim': 'N4S',
  'nemo-4': 'N4',
  'nemo-6': 'N6',
  'nemo-6-xl': 'N6X',
  'nemo-8': 'N8',
  'nemo-9': 'N9',
  
  // Nemo Narozne models
  'nemo-2b': 'N2B',
  'nemo-2b-xl': 'N2BX',
  'nemo-4b-slim': 'N4BS',
  'nemo-4b': 'N4B',
  'nemo-8b': 'N8B',
  
  // Nemo Vis-a-vis AKU models
  'nemo-2-duo': 'N2D',
  'nemo-4-duo': 'N4D',
  'nemo-9-duo': 'N9D'
}

// Reverse lookup for models
export const CODE_TO_MODEL = Object.fromEntries(
  Object.entries(MODEL_CODES).map(([model, code]) => [code, model])
)

// Parse product code and return configuration
export function parseDirectProductCode(productCode: string): {
  modelGroup?: string
  modelKey?: string
  config: Partial<ConfigurationState>
} {
  const parts = productCode.toUpperCase().split('-')
  const result: {
    modelGroup?: string
    modelKey?: string
    config: Partial<ConfigurationState>
  } = {
    config: {}
  }
  
  if (parts.length < 2) {
    return result
  }
  
  // Parse model group (first part)
  const groupCode = parts[0]
  result.modelGroup = CODE_TO_MODEL_GROUP[groupCode]
  
  // Parse model (second part)
  const modelCode = parts[1]
  result.modelKey = CODE_TO_MODEL[modelCode]
  
  // Check if the model is an AKU model and adjust group accordingly
  if (result.modelKey?.includes('aku')) {
    if (result.modelGroup === 'dragon-proste') {
      result.modelGroup = 'dragon-proste-aku'
    } else if (result.modelGroup === 'dragon-narozne') {
      result.modelGroup = 'dragon-narozne-aku'
    } else if (result.modelGroup === 'dragon-vis-a-vis') {
      // Map vis-a-vis AKU to its own group
      result.modelGroup = 'dragon-vis-a-vis-aku'
    }
  }
  
  // Parse configuration options (remaining parts) - all are optional
  const optionCodes = parts.slice(2)
  
  for (const code of optionCodes) {
    // Check for standard handle codes (set variant to standard)
    if (code === 'K1') {
      result.config.handle = 'classic-black'
      result.config.variant = 'standard'
      continue
    }
    if (code === 'K2') {
      result.config.handle = 'classic-silver'
      result.config.variant = 'standard'
      continue
    }
    if (code === 'K7') {
      result.config.handle = 'design-black'
      result.config.variant = 'standard'
      continue
    }
    if (code === 'K5') {
      result.config.handle = 'design-black-silver'
      result.config.variant = 'standard'
      continue
    }
    
    // Check for LIFT handle codes
    if (code === 'KL1') {
      result.config.handle = 'classic-black'
      result.config.variant = 'lift'
      continue
    }
    if (code === 'KL2') {
      result.config.handle = 'classic-silver'
      result.config.variant = 'lift'
      continue
    }
    
    // Check for vis-a-vis front handle codes (LIFT)
    if (code === 'FKL1') {
      result.config['front-handle'] = 'classic-black'
      result.config['front-variant'] = 'lift'
      continue
    }
    if (code === 'FKL2') {
      result.config['front-handle'] = 'classic-silver'
      result.config['front-variant'] = 'lift'
      continue
    }
    
    // Check for vis-a-vis front handle codes (standard)
    if (code === 'FK1') {
      result.config['front-handle'] = 'classic-black'
      continue
    }
    if (code === 'FK2') {
      result.config['front-handle'] = 'classic-silver'
      continue
    }
    if (code === 'FK7') {
      result.config['front-handle'] = 'design-black'
      continue
    }
    if (code === 'FK5') {
      result.config['front-handle'] = 'design-black-silver'
      continue
    }
    
    // Check for vis-a-vis back handle codes (LIFT)
    if (code === 'BKL1') {
      result.config['back-handle'] = 'classic-black'
      result.config['back-variant'] = 'lift'
      continue
    }
    if (code === 'BKL2') {
      result.config['back-handle'] = 'classic-silver'
      result.config['back-variant'] = 'lift'
      continue
    }
    
    // Check for vis-a-vis back handle codes (standard)
    if (code === 'BK1') {
      result.config['back-handle'] = 'classic-black'
      continue
    }
    if (code === 'BK2') {
      result.config['back-handle'] = 'classic-silver'
      continue
    }
    if (code === 'BK7') {
      result.config['back-handle'] = 'design-black'
      continue
    }
    if (code === 'BK5') {
      result.config['back-handle'] = 'design-black-silver'
      continue
    }
    
    // Check for handle on small glass (just 'M')
    if (code === 'M') {
      result.config.handleOnSmallGlass = true
      continue
    }
    
    // Check for fusion modules count (M followed by number)
    if (code.startsWith('M') && code.length > 1) {
      const count = parseInt(code.substring(1), 10)
      if (!isNaN(count)) {
        result.config.fusionModulesCount = count
        if (count > 0) {
          result.config.fusionModules = 'fusion-modules'
        }
        continue
      }
    }
    
    // Check for fusion corner modules count (MC followed by number)
    if (code.startsWith('MC') && code.length > 2) {
      const count = parseInt(code.substring(2), 10)
      if (!isNaN(count)) {
        result.config.fusionCornerModulesCount = count
        if (count > 0) {
          result.config.fusionCornerModules = 'fusion-corner-modules'
        }
        continue
      }
    }
    
    // Check for thermal modules count (T followed by number)
    if (code.startsWith('T') && code.length > 1) {
      const count = parseInt(code.substring(1), 10)
      if (!isNaN(count)) {
        result.config.thermalModulesCount = count
        if (count > 0) {
          result.config.thermalModules = 'thermal'
        }
        continue
      }
    }
    
    // Check for controller buffor (CBUF)
    if (code === 'CBUF') {
      result.config.controllerBuffor = true
      continue
    }
    
    const option = CODE_TO_DIRECT_OPTION.get(code)
    if (option) {
      result.config[option.category] = option.value as any
    }
  }
  
  return result
}

// Generate product code from model and configuration
export function generateDirectProductCode(
  modelGroup: string,
  modelKey: string,
  config: ConfigurationState
): string {
  const parts: string[] = []
  
  // Determine the correct group code based on model type
  let effectiveGroup = modelGroup
  
  // If the model is an AKU model, use the AKU group
  if (modelKey.includes('aku')) {
    if (modelGroup === 'dragon-proste') {
      effectiveGroup = 'dragon-proste-aku'
    } else if (modelGroup === 'dragon-narozne') {
      effectiveGroup = 'dragon-narozne-aku'
    } else if (modelGroup === 'dragon-vis-a-vis') {
      effectiveGroup = 'dragon-vis-a-vis-aku'
    }
  }
  
  // Add model group code
  const groupCode = MODEL_GROUP_CODES[effectiveGroup as keyof typeof MODEL_GROUP_CODES]
  if (groupCode) {
    parts.push(groupCode)
  }
  
  // Add model code
  const modelCode = MODEL_CODES[modelKey as keyof typeof MODEL_CODES]
  if (modelCode) {
    parts.push(modelCode)
  }
  
  // Add configuration option codes
  const optionCodes: string[] = []
  
  // Collect all option codes
  Object.entries(config).forEach(([key, value]) => {
    if (value && value !== '') {
      // Special handling for handle codes based on variant
      if (key === 'handle') {
        const isLift = config.variant === 'lift'
        if (isLift) {
          // Use KL codes for LIFT variant
          if (value === 'classic-black') {
            optionCodes.push('KL1')
          } else if (value === 'classic-silver') {
            optionCodes.push('KL2')
          }
        } else {
          // Use K codes for standard variant
          const optionKey = `${key}:${value}`
          const code = OPTION_TO_DIRECT_CODE.get(optionKey)
          if (code) {
            optionCodes.push(code)
          }
        }
      } else if (key === 'front-handle') {
        // Special handling for vis-a-vis front handle
        const isFrontLift = config['front-variant'] === 'lift'
        if (isFrontLift) {
          if (value === 'classic-black') {
            optionCodes.push('FKL1')
          } else if (value === 'classic-silver') {
            optionCodes.push('FKL2')
          }
        } else {
          // Standard codes FK1, FK2, FK7, FK5
          if (value === 'classic-black') {
            optionCodes.push('FK1')
          } else if (value === 'classic-silver') {
            optionCodes.push('FK2')
          } else if (value === 'design-black') {
            optionCodes.push('FK7')
          } else if (value === 'design-black-silver') {
            optionCodes.push('FK5')
          }
        }
      } else if (key === 'back-handle') {
        // Special handling for vis-a-vis back handle
        const isBackLift = config['back-variant'] === 'lift'
        if (isBackLift) {
          if (value === 'classic-black') {
            optionCodes.push('BKL1')
          } else if (value === 'classic-silver') {
            optionCodes.push('BKL2')
          }
        } else {
          // Standard codes BK1, BK2, BK7, BK5
          if (value === 'classic-black') {
            optionCodes.push('BK1')
          } else if (value === 'classic-silver') {
            optionCodes.push('BK2')
          } else if (value === 'design-black') {
            optionCodes.push('BK7')
          } else if (value === 'design-black-silver') {
            optionCodes.push('BK5')
          }
        }
      } else if (key === 'vis-a-vis-serviceHandleSide') {
        // Handle vis-a-vis service handle side with VSL/VSR codes
        const optionKey = `${key}:${value}`
        const code = OPTION_TO_DIRECT_CODE.get(optionKey)
        if (code) {
          optionCodes.push(code)
        }
      } else if (key === 'controllerBuffor') {
        // Handle controllerBuffor (boolean) - only add CBUF code if true
        // Controller code itself is added by normal 'controller' key handling
        if (value === true) {
          optionCodes.push('CBUF')
        }
      } else {
        // Normal handling for other options
        const optionKey = `${key}:${value}`
        const code = OPTION_TO_DIRECT_CODE.get(optionKey)
        if (code) {
          optionCodes.push(code)
        }
      }
    }
  })
  
  // Add handle on small glass code (M) for corner models
  if (config.handleOnSmallGlass === true) {
    optionCodes.push('M')
  }
  
  // Add fusion modules count if fusion modules are selected
  if (config.fusionModules === 'fusion-modules' && config.fusionModulesCount > 0) {
    optionCodes.push(`M${config.fusionModulesCount}`)
  }
  
  // Add fusion corner modules count if fusion corner modules are selected
  if (config.fusionCornerModules === 'fusion-corner-modules' && config.fusionCornerModulesCount > 0) {
    optionCodes.push(`MC${config.fusionCornerModulesCount}`)
  }
  
  // Add thermal modules count if thermal modules are selected
  if (config.thermalModules === 'thermal' && config.thermalModulesCount && config.thermalModulesCount > 0) {
    optionCodes.push(`T${config.thermalModulesCount}`)
  }
  
  // Sort option codes for consistency
  optionCodes.sort()
  parts.push(...optionCodes)
  
  return parts.join('-')
}

// Apply direct options to configuration (bulk update without cascade)
export function applyDirectOptions(
  config: ConfigurationState,
  options: Partial<ConfigurationState>
): ConfigurationState {
  return {
    ...config,
    ...options
  }
}

// Find model data by group and key
export function findModelData(
  modelsData: ModelsData,
  modelGroup: string,
  modelKey: string
): ModelData | null {
  const group = modelsData[modelGroup]
  if (!group) return null
  
  return group[modelKey] || null
}
